<template>
  <div
    class="sf-tab-item sf-hover"
    data-testid="export-item"
    :data-export-id="exportItem.id"
  >
    <div class="sf-grid sf-grid-fluid sf-grid-gutter-none">
      <div class="sf-row tw-items-center">
        <div
          class="
            sf-col sf-xs-12 sf-sm-4
            tw-flex tw-flex-row tw-gap-4 tw-items-center
          "
        >
          <SfCheckbox
            :model-value="selected"
            @update:model-value="emit('toggle-selected', exportItem.id)"
          />
          <a
            v-if="succeeded"
            :href="exportItem.url"
            class="sf-a sf-a-primary"
            target="_blank"
          >
            {{ exportItem.title }}
          </a>
          <div v-if="!succeeded">{{ exportItem.title }}</div>
        </div>

        <div class="sf-col sf-xs-12 sf-sm-3 sf-pad-l-1 sf-break-word">
          {{ exportItem.exportedBy }}
        </div>

        <div class="sf-col sf-xs-12 sf-sm-3 sf-pad-l-1">
          <span>{{ timestamp }}</span>
        </div>

        <div class="sf-col sf-xs-12 sf-sm-2 sf-level sf-level-right sf-nowrap">
          <i
            v-if="processing"
            class="fa fa-spinner fa-spin export-spinner"
            data-testid="spinner-processing"
          />
          <i
            v-if="retrying"
            v-tooltip="retryingTooltip"
            class="fa fa-spinner fa-spin export-spinner sf-c-danger-light-1"
            data-testid="spinner-retrying"
          />
          <i
            v-if="failed"
            class="fa fa-exclamation-triangle sf-c-danger-light-1"
            data-testid="spinner-failed "
          />
          <span class="tw-flex tw-flex-row">
            <ExportItemAction
              v-if="exportItem.debugLog"
              tooltip="View debug log"
              :disabled="anySelected"
              data-testid="export-debug-log-button"
              icon="bug"
              @click="showDebugModal = true"
            />
            <ExportItemAction
              v-if="succeeded"
              :tooltip="I18n.t('js.export.copy_to_documents')"
              :disabled="anySelected"
              data-testid="export-item-move-to-documents"
              icon="folder-open"
              @click="moveHandler"
            />
            <template v-if="succeeded && appLinks.length == 1">
              <ExportItemAction
                :tooltip="appLinks[0].name"
                :disabled="anySelected"
                :data-testid="`export-item-applink-${appLinks[0].name}`"
                icon="link"
                @click="
                  appLinkHandler(appLinks[0].url, appLinks[0].openInNewTab)
                "
              />
            </template>
            <ExportItemAction
              v-if="!processing && !exportItem.clientMeetingId"
              :tooltip="I18n.t('js.export.regenerate')"
              :disabled="anySelected"
              data-testid="export-item-regenerate"
              icon="arrows-rotate"
              @click="regenerateHandler"
            />
            <ExportItemAction
              :tooltip="I18n.t('confirmation.delete')"
              :disabled="anySelected"
              data-testid="export-item-delete"
              icon="trash"
              @click="deleteHandler()"
            />
            <SfDropdown
              v-if="succeeded && appLinks.length > 1"
              :dropdown-id="dropdownId"
              :is-open="dropdownExpanded"
              :disabled="anySelected"
              trigger-size="small"
              variant="seamless"
              unstyled-trigger
              :drawer-direction="drawerDirections.left"
              @dropdown-clicked="handleToggleDropdown"
              @dropdown-closed="handleCloseDropdown"
            >
              <template #trigger>
                <ExportItemAction icon="ellipsis-v" :disabled="anySelected" />
              </template>

              <template #menu>
                <a
                  v-for="appLink in appLinks"
                  :key="appLink.name"
                  class="sf-tab-item sf-block sf-full sf-a"
                  @click.stop.prevent="
                    appLinkHandler(appLink.url, appLink.openInNewTab)
                  "
                >
                  <i class="fa fa-link sf-pad-r-1"></i>
                  {{ appLink.name }}
                </a>
              </template>
            </SfDropdown>
          </span>
        </div>
      </div>

      <div v-if="hasMessages" class="div">
        <div class="sf-below-2"></div>
        <div class="sf-row">
          <div class="sf-col sf-xs-12">
            <div
              :class="{
                'sf-alert-danger': failed || retrying,
                'sf-alert-warning': succeeded,
              }"
              class="sf-alert sf-pad-2"
            >
              <p
                v-for="(errorMessage, index) in exportItem.errorMessages"
                :key="`${errorMessage}-${index}`"
                v-dompurify-html="errorMessage"
                :class="{
                  'sf-below-0': index == exportItem.errorMessages.length - 1,
                }"
              ></p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SfModal
      v-if="showDebugModal && exportItem.debugLog"
      size="l"
      data-testid="export-debug-modal"
      @close="showDebugModal = false"
    >
      <template #title>Export Debug Log (ID: {{ exportItem.id }})</template>
      <template #body>
        <ExportDebugLog :debug-log="exportItem.debugLog" />
      </template>
    </SfModal>
  </div>
</template>

<script setup lang="ts">
import type { AppLink, ExportType } from '@app/application/export/types'

import { computed, ref } from 'vue'

import api from '../api/export-item'
import { dropdowns } from '@app/shared/vue/store/modules/dropdown/constants'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfModal from '@app/shared/vue/components/sf-modal/sf-modal.vue'
import formatTimestamp from '@app/utils/time-formatter'
import { drawerDirections } from '@app/shared/vue/components/sf-dropdown/constants'
import ExportItemAction from '@app/application/export/screens/exports/components/export-item-action.vue'
import ExportDebugLog from '@app/application/export/screens/exports/components/export-debug-log.vue'
import { SfCheckbox } from '@silverfin/ui-components'

defineOptions({
  name: 'ExportItem',
})

interface Props {
  appLinks: AppLink[]
  dropdownExpanded: boolean
  exportItem: ExportType
  selected: boolean
  anySelected: boolean
}

const I18n = window.I18n
const dropdownId = dropdowns.EXPORT
const retryingTooltip = I18n.t('js.export.retrying')

const props = defineProps<Props>()

const emit =
  defineEmits<{
    'close-dropdown': []
    'toggle-dropdown': [number]
    'toggle-selected': [number]
  }>()

const showDebugModal = ref(false)

const failed = computed(() => props.exportItem.status === 'failed')
const hasMessages = computed(
  () =>
    props.exportItem.errorMessages.length > 0 &&
    !['started', 'processing'].includes(props.exportItem.status)
)
const processing = computed(
  () =>
    ['started', 'processing'].includes(props.exportItem.status) &&
    !hasMessages.value
)
const retrying = computed(() => props.exportItem.status === 'retrying')
const succeeded = computed(() => props.exportItem.status === 'succeeded')
const timestamp = computed(() =>
  formatTimestamp(props.exportItem.createdAt, {
    shouldUseLocalTimezone: true,
    shouldUseRelativeFormatOnCurrentDate: false,
  })
)

function moveHandler() {
  return api.moveExport(props.exportItem.id)
}

function appLinkHandler(url: string, openInNewTab: boolean) {
  const fullUrl =
    url +
    '&object_id=' +
    props.exportItem.id +
    '&period_id=' +
    props.exportItem.ledgerId
  if (openInNewTab) {
    window.open(fullUrl, '_blank')?.focus()
  } else {
    window.Turbolinks.visit(fullUrl)
  }
}

function regenerateHandler() {
  window.Turbolinks.visit(props.exportItem.links.copy)
}

function deleteHandler() {
  window.sfRemoveConfirm({
    name: props.exportItem.title,
    onConfirm: () => {
      api.deleteExport(props.exportItem.id)
    },
    keep: false,
  })
}

function handleCloseDropdown() {
  emit('close-dropdown')
}

function handleToggleDropdown() {
  emit('toggle-dropdown', props.exportItem.id)
}
</script>
